import type { jsPDF } from "jspdf";
import { drawQRCode } from "./drawQRCode";
import { PDF_FONT } from "./pdfFonts";
import type { PDFBitmap } from "./pdfImages";
import {
  FIGURE_OFFSET,
  PDF_COLORS,
  PDF_FOOTER_BASELINE,
  PDF_HEADER,
  PDF_IMAGE_BOX,
  PDF_MARGIN,
  PDF_PAGE,
  PDF_RULES,
  PDF_SPEC,
  PDF_TITLE,
  PDF_TYPE,
  SPEC_RHYTHM,
  SPEC_ROWS,
} from "./pdfTheme";

/**
 * The document's own wording, handed in already translated.
 *
 * `utils/` does not reach for the locale layer on its own for the same reason
 * it does not reach for the catalog: it lays out whatever it is given.
 */
export type OfferCopy = {
  specification: string;
  quantity: string;
  total: string;
  /** Caption under the printed QR code. */
  arHint: string;
  /** Footer line, with the date already in it. */
  offerDate: string;
};

/**
 * One printed specification line, already resolved down to strings.
 *
 * Nothing here names a domain type on purpose: `utils/` knows nothing about the
 * catalog (see Architecture in AGENTS.md), so the feature formats its money and
 * resolves its options first, and this module only lays the result out.
 */
export type OfferSpecRow = {
  /** Artwork key, `groupId:optionId` — the same key `artwork` is built on. */
  key: string;
  label: string;
  /** Swatch fill, for colour/material groups without artwork. */
  color?: string;
  /** Surcharge as it prints, e.g. "+ 14 EUR". Absent when the option is included. */
  surcharge?: string;
};

export type OfferSpecGroup = {
  label: string;
  rows: OfferSpecRow[];
};

/**
 * Everything the document draws, already resolved: no store access and no image
 * loading past this point.
 *
 * Splitting it out keeps the drawing pass synchronous — jsPDF cannot draw
 * asynchronously, and interleaving awaits with the row cursor is how positions
 * drift — and lets the layout be rendered outside a browser when checking it
 * against the design.
 */
export type OfferContent = {
  title: string;
  copy: OfferCopy;
  /** Link the printed QR code resolves to (the AR hand-off URL). */
  arShareUrl: string;
  logo: PDFBitmap | null;
  productRender: PDFBitmap | null;
  groups: OfferSpecGroup[];
  /** Row artwork, keyed `groupId:optionId`. */
  artwork: Map<string, PDFBitmap>;
  /** Quantity as it prints, e.g. "2 Ks". */
  quantity: string;
  /** Total as it prints, e.g. "3 811 EUR". */
  total: string;
  /** Domain printed bottom right; omitted leaves the footer with only the date. */
  website?: string;
};

function setText(
  doc: jsPDF,
  style: { size: number; weight: string },
  color: string,
): void {
  doc.setFont(PDF_FONT, style.weight);
  doc.setFontSize(style.size);
  doc.setTextColor(color);
}

function drawRule(
  doc: jsPDF,
  y: number,
  rule: { width: number; color: string },
): void {
  doc.setLineWidth(rule.width);
  doc.setDrawColor(rule.color);
  doc.line(PDF_MARGIN.contentLeft, y, PDF_MARGIN.contentRight, y);
}

function drawRowText(
  doc: jsPDF,
  row: OfferSpecRow,
  top: number,
  geometry: RowGeometry,
): void {
  // The label is centred on the row rather than pinned to its top: the row
  // variants differ in height and the design aligns each against its own
  // artwork. The surcharge then hangs off the label's baseline.
  const labelBaseline =
    top + geometry.height / 2 + PDF_TYPE.optionValue.size * 0.35;

  setText(doc, PDF_TYPE.optionValue, PDF_COLORS.grey600);
  doc.text(row.label, geometry.labelX, labelBaseline);

  // The design's swatch row has no price slot: a colour or material choice is
  // never priced on the page, only counted into the total.
  if (!row.surcharge || geometry === SPEC_ROWS.swatch) return;
  setText(doc, PDF_TYPE.optionPrice, PDF_COLORS.figure);
  doc.text(
    row.surcharge,
    PDF_MARGIN.contentRight,
    labelBaseline + FIGURE_OFFSET.surcharge,
    { align: "right" },
  );
}

type RowGeometry = { height: number; labelX: number };

/**
 * A colour or material group. Its artwork is the swatch's own fill, clipped into
 * the circle, rather than a photo laid out beside the label — which is why the
 * test is the colour and not the presence of artwork.
 */
export function isSwatchGroup(group: OfferSpecGroup): boolean {
  return group.rows.some((row) => row.color);
}

/**
 * Picks the row geometry for a whole group rather than per row, so every row in
 * it shares one label column: a group where only some options have artwork would
 * otherwise print as a ragged list.
 */
function resolveGroupGeometry(
  group: OfferSpecGroup,
  artwork: OfferContent["artwork"],
): RowGeometry {
  if (isSwatchGroup(group)) return SPEC_ROWS.swatch;
  if (group.rows.some((row) => artwork.has(row.key)))
    return SPEC_ROWS.thumbnail;
  return SPEC_ROWS.text;
}

/* The swatch circle sits centred in its column, and falls back to a flat fill of
 * the option's colour where the config ships no artwork for it. */
function drawSwatch(
  doc: jsPDF,
  row: OfferSpecRow,
  top: number,
  artwork: PDFBitmap | undefined,
): void {
  const { diameter, columnWidth } = SPEC_ROWS.swatch;
  const x = PDF_MARGIN.contentLeft + (columnWidth - diameter) / 2;

  if (artwork) {
    doc.addImage(artwork.dataUrl, "PNG", x, top, diameter, diameter);
    return;
  }
  if (!row.color) return;
  const radius = diameter / 2;
  doc.setFillColor(row.color);
  doc.circle(x + radius, top + radius, radius, "F");
}

/** Draws one specification row into `geometry` and returns the height it took. */
function drawRow(
  doc: jsPDF,
  row: OfferSpecRow,
  top: number,
  geometry: RowGeometry,
  artwork: PDFBitmap | undefined,
): number {
  if (geometry === SPEC_ROWS.swatch) {
    drawSwatch(doc, row, top, artwork);
  } else if (artwork) {
    doc.addImage(
      artwork.dataUrl,
      "PNG",
      PDF_MARGIN.contentLeft,
      top + (geometry.height - artwork.height) / 2,
      artwork.width,
      artwork.height,
    );
  }

  drawRowText(doc, row, top, geometry);
  return geometry.height;
}

/* jsPDF resolves `splitTextToSize` through an index signature, so its own types
 * hand back `any`. Declaring the one method here keeps the call site typed
 * instead of spreading `any` into the title layout.
 *
 * Wrapping is not cosmetic: the design pins the product render at x 224, and
 * `Kancelářská židle` is 197pt wide at this size — it would print over it. */
type TextWrapper = {
  splitTextToSize: (text: string, maxWidth: number) => string[];
};

function wrapTitle(doc: jsPDF, title: string): string[] {
  return (doc as unknown as TextWrapper).splitTextToSize(
    title,
    PDF_TITLE.maxWidth,
  );
}

function drawHeader(doc: jsPDF, content: OfferContent): void {
  if (content.logo) {
    doc.addImage(
      content.logo.dataUrl,
      "PNG",
      PDF_HEADER.logo.x,
      PDF_HEADER.logo.y,
      content.logo.width,
      content.logo.height,
    );
  }

  drawQRCode(
    doc,
    content.arShareUrl,
    {
      x: PDF_HEADER.qr.right - PDF_HEADER.qr.size,
      y: PDF_HEADER.qr.y,
      size: PDF_HEADER.qr.size,
    },
    PDF_COLORS.rule,
  );

  setText(doc, PDF_TYPE.qrCaption, PDF_COLORS.grey600);
  doc.text(
    content.copy.arHint,
    PDF_HEADER.qr.right,
    PDF_HEADER.qrCaptionBaseline,
    { align: "right" },
  );
}

/**
 * Draws the specification block and returns the y of the heavy rule that closes
 * it, which the quantity and total rows below are positioned from.
 */
function drawSpecification(doc: jsPDF, content: OfferContent): number {
  setText(doc, PDF_TYPE.sectionHeading, PDF_COLORS.grey900);
  doc.text(
    content.copy.specification,
    PDF_MARGIN.contentLeft,
    PDF_SPEC.headingBaseline,
  );
  drawRule(doc, PDF_SPEC.headingRuleY, PDF_RULES.heavy);

  let ruleY = PDF_SPEC.headingRuleY;
  let extraLeading: number = SPEC_RHYTHM.firstGroupExtraLeading;

  content.groups.forEach((group, groupIndex) => {
    const labelBaseline =
      ruleY + SPEC_RHYTHM.ruleToLabelBaseline + extraLeading;
    extraLeading = 0;

    setText(doc, PDF_TYPE.groupLabel, PDF_COLORS.grey900);
    doc.text(group.label, PDF_MARGIN.contentLeft, labelBaseline);

    const geometry = resolveGroupGeometry(group, content.artwork);
    let rowTop = labelBaseline + SPEC_RHYTHM.labelBaselineToRowTop;
    let rowBottom = rowTop;

    group.rows.forEach((row, rowIndex) => {
      if (rowIndex > 0) rowTop = rowBottom + SPEC_RHYTHM.rowGap;
      rowBottom =
        rowTop +
        drawRow(doc, row, rowTop, geometry, content.artwork.get(row.key));
    });

    const isLastGroup = groupIndex === content.groups.length - 1;
    ruleY =
      rowBottom +
      (isLastGroup
        ? SPEC_RHYTHM.rowBottomToBlockRule
        : SPEC_RHYTHM.rowBottomToGroupRule);
    drawRule(doc, ruleY, isLastGroup ? PDF_RULES.heavy : PDF_RULES.light);
  });

  return ruleY;
}

function drawTotals(doc: jsPDF, content: OfferContent, blockRuleY: number) {
  const quantityBaseline = blockRuleY + SPEC_RHYTHM.blockRuleToQuantityBaseline;
  setText(doc, PDF_TYPE.quantityLabel, PDF_COLORS.grey900);
  doc.text(content.copy.quantity, PDF_MARGIN.contentLeft, quantityBaseline);
  setText(doc, PDF_TYPE.quantityValue, PDF_COLORS.figure);
  doc.text(
    content.quantity,
    PDF_MARGIN.contentRight,
    quantityBaseline + FIGURE_OFFSET.quantity,
    { align: "right" },
  );

  // Light, not heavy: the quantity row is a line inside the totals block rather
  // than a boundary around it.
  const totalRuleY = quantityBaseline + SPEC_RHYTHM.quantityBaselineToRule;
  drawRule(doc, totalRuleY, PDF_RULES.light);

  // The total's value is the anchored one and its label sits slightly above it.
  const totalBaseline = totalRuleY + SPEC_RHYTHM.ruleToTotalBaseline;
  setText(doc, PDF_TYPE.totalLabel, PDF_COLORS.grey600);
  doc.text(
    content.copy.total,
    PDF_MARGIN.contentLeft,
    totalBaseline + FIGURE_OFFSET.totalLabel,
  );
  setText(doc, PDF_TYPE.totalValue, PDF_COLORS.figure);
  doc.text(content.total, PDF_MARGIN.contentRight, totalBaseline, {
    align: "right",
  });

  drawRule(
    doc,
    totalBaseline + SPEC_RHYTHM.totalBaselineToRule,
    PDF_RULES.heavy,
  );
}

function drawFooter(doc: jsPDF, content: OfferContent): void {
  setText(doc, PDF_TYPE.footer, PDF_COLORS.grey900);
  doc.text(content.copy.offerDate, PDF_MARGIN.outer, PDF_FOOTER_BASELINE);

  if (!content.website) return;
  setText(doc, PDF_TYPE.footerBrand, PDF_COLORS.grey900);
  doc.text(
    content.website.toUpperCase(),
    PDF_PAGE.width - PDF_MARGIN.outer,
    PDF_FOOTER_BASELINE,
    { align: "right" },
  );
}

/** Lays the whole offer out on `doc`, top to bottom. */
export function drawOfferDocument(doc: jsPDF, content: OfferContent): void {
  drawHeader(doc, content);

  setText(doc, PDF_TYPE.title, PDF_COLORS.figure);
  // Wrapped inside the design's title box and drawn line by line: the first
  // baseline stays pinned where the design puts it and extra lines grow
  // downwards, the way the design's auto-height frame does.
  wrapTitle(doc, content.title).forEach((line, index) => {
    doc.text(
      line,
      PDF_MARGIN.contentLeft,
      PDF_TITLE.baseline + index * PDF_TITLE.lineHeight,
    );
  });

  if (content.productRender) {
    doc.addImage(
      content.productRender.dataUrl,
      "PNG",
      PDF_IMAGE_BOX.centerX - content.productRender.width / 2,
      PDF_IMAGE_BOX.centerY - content.productRender.height / 2,
      content.productRender.width,
      content.productRender.height,
    );
  }

  drawTotals(doc, content, drawSpecification(doc, content));
  drawFooter(doc, content);
}
